import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { isPast } from 'date-fns';
import { times } from 'lodash';
import uniq from 'lodash/uniq';
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronUp, Edit, Eye, EyeOff, Image, Plus, Trash } from 'react-feather';
import { Controller, useFieldArray, UseFieldArrayReturn, useForm, UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useMutation, useServerErrors } from '../../../Shared/api';
import ImageCropper from '../../../Shared/Image/ImageCropper';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { CampaignField, CustomField, CustomFieldChoice, CustomFieldType, donationFields, Event, Locale, Ticket, User } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import { ConfirmButton } from '../../../Shared/UI/ConfirmButton';
import { DateInput } from '../../../Shared/UI/DateInput';
import DropdownMenu from '../../../Shared/UI/DropdownMenu';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { FadeIn } from '../../../Shared/UI/FadeIn';
import { FileInput } from '../../../Shared/UI/FileInput';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputDescription from '../../../Shared/UI/InputDescription';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { Radio } from '../../../Shared/UI/Radio';
import RadioLabel from '../../../Shared/UI/RadioLabel';
import { Select } from '../../../Shared/UI/Select';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { Textarea } from '../../../Shared/UI/Textarea';
import { useRouter } from '../../router';
import { Charity, Project } from '../../types';

export interface EditProjectFormProps {
  project: Project & {
    event: (Event & {
      tickets: Ticket[];
    }) | null;
    custom_fields: (CustomField & {
      deletable: boolean;
      choices: ({
        deletable: boolean;
      } & CustomFieldChoice)[];
    })[];
    new_campaign_notification_users: User[];
    team_share_excluded_tickets: Ticket[];
  };
  users: User[];
  charity: Charity;
}

const useEditProjectSchema = () => useMemo(() => {
  const schema = yup.object({
    title: yup.string().required(),
    description: yup.string().ensure().nullable(),
    image: yup.string().ensure().nullable(),
    delete_image: yup.boolean().required(),
    mail_sender: yup.string().ensure().nullable(),
    mail_header: yup.string().ensure().nullable(),
    delete_mail_header: yup.boolean().required(),
    website: yup.string().ensure().nullable(),
    email: yup.string().ensure().nullable(),
    fields: yup.array(yup.string().required()).required(),
    required_fields: yup.array(yup.string().required()).required(),
    custom_fields: yup.array(yup.object({
      id: yup.string().nullable(),
      title: yup.string().required(),
      description: yup.string().ensure().nullable(),
      type: yup.string().required(),
      required: yup.boolean().required().required(),
      enabled: yup.boolean().required().required(),
      choices: yup.array(yup.object({
        id: yup.string().nullable(),
        value: yup.string().required(),
      })).required(),
    })).required(),
    donation_fields: yup.array(yup.string().required()).required(),
    terms_url: yup.string().ensure().nullable(),
    default_individual_description: yup.string().ensure().nullable(),
    default_team_description: yup.string().ensure().nullable(),
    donation_message: yup.string().ensure().nullable(),
    open: yup.boolean().required(),
    allow_individuals: yup.boolean().required(),
    allow_teams: yup.boolean().required(),
    private: yup.boolean().required(),
    team_share_excluded_tickets: yup.array(yup.string().required()).required(),
    locale: yup.string().required(),
    campaign_code: yup.string().ensure().nullable(),
    payment_reference: yup.string().ensure().nullable(),
    end_date: yup.string().ensure().nullable(),
    new_campaign_notification_users: yup.array(yup.string().ensure().nullable()).required(),
  });

  return {
    schema,
    resolver: yupResolver(schema),
  };
}, []);

export type EditProjectFormData = yup.InferType<ReturnType<typeof useEditProjectSchema>['schema']>;

export default function EditProjectForm({
  project,
  users,
  charity,
}: EditProjectFormProps) {
  const { t } = useTranslation();
  const { parseDate, formatDate } = useLocale();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const { resolver } = useEditProjectSchema();

  const form = useForm({
    defaultValues: {
      title: project.title,
      description: project.description,
      image: null,
      delete_image: !project.image_url,
      mail_sender: project.mail_sender,
      mail_header: null,
      delete_mail_header: false,
      website: project.website,
      email: project.email,
      fields: project.fields,
      required_fields: project.required_fields,
      custom_fields: project.custom_fields.map((field) => ({
        id: field.id,
        title: field.title,
        type: field.type,
        description: field.description,
        choices: field.choices.map((choice) => ({
          id: choice.id,
          value: choice.value,
        })),
        required: field.required,
        enabled: field.enabled,
      })),
      donation_fields: project.donation_fields,
      terms_url: project.terms_url,
      default_individual_description: project.default_individual_description,
      default_team_description: project.default_team_description,
      donation_message: project.donation_message,
      open: project.open,
      allow_individuals: project.allow_individuals,
      allow_teams: project.allow_teams,
      private: project.private,
      team_share_excluded_tickets: project.team_share_excluded_tickets.map((ticket) => ticket.id),
      locale: project.locale,
      campaign_code: project.campaign_code,
      payment_reference: project.payment_reference,
      end_date: project.end_date ? formatDate(project.end_date, 'internal_date') : null,
      new_campaign_notification_users: project.new_campaign_notification_users.map((user) => user.id),
    },
    resolver,
  });

  const {
    register, setValue, handleSubmit, setError, resetField, watch, control,
    formState: { errors, defaultValues },
  } = form;

  const [editProject, { errors: serverErrors, loading, formKey }] = useMutation(
    routes.edit_project(project.id),
    {
      onSuccess: () => {
        notifier.notify(t('backend:project_saved'));
      },
    }
  );

  useServerErrors(form, serverErrors);

  const customFieldsArray = useFieldArray({
    control,
    name: 'custom_fields',
  });

  const [image, setImage] = useState<File>();
  const [croppedImage, setCroppedImage] = useState<File>();
  const deleteImage = watch('delete_image');
  const canDeleteImage = !deleteImage && (!!image || !!project.image_url);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setImage(event.target.files?.[0] || undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', false, { shouldDirty: true });
  };

  const handleCropChange = (file: File, element: HTMLImageElement) => {
    if (element.naturalWidth < 800 || element.naturalHeight < 450) {
      resetImage();
      setError('image', { message: t('shared:validation.image_too_small') });
    } else {
      setCroppedImage(file);
    }
  };

  const resetImage = () => {
    setImage(undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', true, { shouldDirty: true });
  };

  const currentMailHeaderUrl = project.mail_header_url || charity.mail_header_url;
  const [mailHeader, setMailHeader] = useState<File>();
  const deleteMailHeader = watch('delete_mail_header');
  const canDeleteMailHeader = !deleteMailHeader && (!!mailHeader || !!project.mail_header_url);

  const handleMailHeaderChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMailHeader(event.target.files?.[0] || undefined);
    resetField('mail_header'); // Reset errors
    setValue('delete_mail_header', false);
    setMailHeaderSrc(undefined);
  };

  const unsetMailHeader = () => {
    setMailHeader(undefined);
    resetField('mail_header'); // Reset errors
    setValue('delete_mail_header', true);
    setMailHeaderSrc(undefined);
  };

  const [mailHeaderSrc, setMailHeaderSrc] = useState<string>();

  useEffect(() => {
    if (mailHeader) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setMailHeaderSrc(reader.result?.toString() || undefined));
      reader.readAsDataURL(mailHeader);
    }
  }, [mailHeader]);

  const validateMailHeader = (event: SyntheticEvent<HTMLImageElement>) => {
    const element = event.currentTarget;

    if (element.naturalWidth < 570) {
      unsetMailHeader();
      setError('mail_header', { message: t('shared:validation.image_too_small') });
    }
  };

  const allowIndividuals = watch('allow_individuals');
  const allowTeams = watch('allow_teams');

  const enabledFields = watch('fields');
  const enabledRequiredFields = watch('required_fields');

  const open = watch('open');
  const endDate = watch('end_date');
  const parsedEndDate = endDate ? parseDate(endDate, 'internal_date') : null;
  const endDateIsPast = !!parsedEndDate && isPast(parsedEndDate);

  const paymentReference = watch('payment_reference');
  const paymentReferenceLength = paymentReference?.length || 0;
  const maxPaymentReferenceLength = 20;

  const projectTickets = project.event?.tickets || [];

  const disableDefaultField = (field: CampaignField) => {
    setValue('fields', enabledFields.filter((enabledField) => enabledField !== field));
    setValue('required_fields', enabledRequiredFields.filter((enabledField) => enabledField !== field));
  };

  const enableDefaultField = (field: CampaignField) => {
    setValue('fields', uniq(enabledFields.concat([field])));
    setValue('required_fields', enabledRequiredFields.filter((enabledField) => enabledField !== field));
  };

  const requireDefaultField = (field: CampaignField) => {
    setValue('fields', uniq(enabledFields.concat([field])));
    setValue('required_fields', uniq(enabledRequiredFields.concat([field])));
  };

  const addCustomField = () => {
    customFieldsArray.append({
      id: null,
      title: '',
      type: CustomFieldType.ShortText,
      description: '',
      choices: [],
      required: false,
      enabled: true,
    });
  };

  const submit = async (data: EditProjectFormData) => {
    await editProject({
      ...data,
      image: (image && croppedImage) || null,
      mail_header: mailHeader,
      required_fields: data?.required_fields?.filter((field) => data?.fields?.includes(field)),
      end_date: data?.end_date,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)} key={formKey}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_project_settings')}
          </h3>
        </div>

        {serverErrors && (
          <div>
            <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />
          </div>
        )}

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:project')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel required valid={!errors.title} htmlFor="edit_project_title">
                {t('backend:project_title')}
              </InputLabel>
              <Input
                {...register('title',)}
                id="edit_project_title"
                aria-invalid={!!errors.title}
              />
              <ErrorMessage error={errors.title} attribute={t('backend:project_title')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_description">
                {t('backend:project_description')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_description_description')}
              </InputDescription>
              <Textarea
                {...register('description')}
                id="edit_project_description"
                aria-invalid={!!errors.title}
                rows={3}
              />
              <ErrorMessage error={errors.description} attribute={t('backend:project_description')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_website">
                {t('backend:website')}
              </InputLabel>
              <Input
                {...register('website')}
                id="edit_project_website"
                aria-invalid={!!errors.website}
              />
              <ErrorMessage error={errors.website} attribute={t('backend:website')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:campaigns')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.allow_individuals && !errors.allow_teams}>
                {t('backend:campaign_types')}
              </InputLabel>
              <InputDescription>
                {t('backend:allowed_campaign_types_description')}
              </InputDescription>
              <CheckboxLabel htmlFor="edit_project_allow_individuals">
                <Checkbox
                  {...register('allow_individuals')}
                  id="edit_project_allow_individuals"
                  disabled={allowIndividuals && !allowTeams}
                  className="mt-1"
                />
                <span>
                  {t('backend:individual_campaigns')}
                </span>
              </CheckboxLabel>
              <CheckboxLabel htmlFor="edit_project_allow_teams">
                <Checkbox
                  {...register('allow_teams')}
                  id="edit_project_allow_teams"
                  disabled={!allowIndividuals && allowTeams}
                  className="mt-1"
                />
                <span>
                  {t('backend:team_campaigns')}
                </span>
              </CheckboxLabel>
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.fields} htmlFor="edit_project_fields">
                {t('backend:project_fields')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_fields_description')}
              </InputDescription>
            </InputGroup>
            <div className="space-y-1">
              {project.allowed_fields.map((campaignField) => {
                const requiredLabel = enabledRequiredFields.includes(campaignField)
                  ? t('backend:required')
                  : (enabledFields.includes(campaignField)
                    ? t('backend:optional')
                    : t('backend:off'));

                return (
                  <div className="grid grid-cols-[5fr_2fr_2fr_96px] gap-4 items-center" key={campaignField}>
                    <div className={classNames('font-medium', !enabledFields.includes(campaignField) && 'text-slate-400')}>
                      {t(`backend:campaign_fields.${campaignField}`)}
                    </div>
                    <div />
                    <div>
                      <DropdownMenu
                        trigger={({ onClick, isOpen }) => (
                          <Button
                            onClick={onClick}
                            variant="tertiary"
                            className={classNames(isOpen && '!bg-slate-200 !border-slate-200')}
                            size="sm"
                            aria-label={`${requiredLabel} (${t(`backend:campaign_fields.${campaignField}`)})`}
                          >
                            <Icon className="mr-1">
                              {enabledRequiredFields.includes(campaignField)
                                ? <Eye />
                                : (enabledFields.includes(campaignField)
                                  ? <Eye />
                                  : <EyeOff />)}
                            </Icon>
                            {requiredLabel}
                            <Icon className="ml-1">
                              <ChevronDown />
                            </Icon>
                          </Button>
                        )}
                        align="start"
                      >
                        <DropdownMenu.Item>
                          <a
                            onClick={() => enableDefaultField(campaignField)}
                            role="button"
                          >
                            {t('backend:optional')}
                          </a>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item>
                          <a
                            onClick={() => requireDefaultField(campaignField)}
                            role="button"
                          >
                            {t('backend:required')}
                          </a>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item>
                          <a
                            onClick={() => disableDefaultField(campaignField)}
                            role="button"
                          >
                            {t('backend:off')}
                          </a>
                        </DropdownMenu.Item>
                      </DropdownMenu>
                    </div>
                    <div/>
                  </div>
                );
              })}

              {customFieldsArray.fields.map((field, index) => (
                <CustomFieldForm
                  form={form}
                  customFieldsArray={customFieldsArray}
                  index={index}
                  project={project}
                  key={field.id}
                />
              ))}
            </div>

            <div>
              <Button onClick={() => addCustomField()} variant="secondary">
                <Icon className="mr-1">
                  <Plus />
                </Icon>
                {t('backend:add_field')}
              </Button>
            </div>

            <InputGroup>
              <InputLabel htmlFor="edit_image">
                {t('backend:image')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_image_description')}
              </InputDescription>
              <div className="max-w-[610px] p-4 border rounded-md border-slate-250">
                <div
                  className="flex items-center justify-center mb-4 transition-all rounded-sm bg-slate-50 aspect-video"
                >
                  {!image && !deleteImage && project.image_url && (
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="object-cover w-full h-full rounded-sm"
                    />
                  )}
                  {image && (
                    <ImageCropper
                      image={image}
                      onChange={handleCropChange}
                      aspect={16 / 9}
                    />
                  )}
                  {((!image && !project.image_url) || deleteImage) && (
                    <Icon className="!w-12 !h-12 text-slate-250">
                      <Image strokeWidth={1} />
                    </Icon>
                  )}
                </div>
                <div className="flex space-x-2">
                  <FileInput
                    {...register('image')}
                    accept=".jpg,.jpeg,.png"
                    id="edit_image"
                    aria-invalid={!!errors.image}
                    onChange={handleImageChange}
                  >
                    <Icon className="mr-1">
                      <Image />
                    </Icon>
                    {t('shared:form.select')}
                  </FileInput>
                  {canDeleteImage && (
                    <Button onClick={() => resetImage()} variant="tertiary" size="sm">
                      <Icon className="mr-1">
                        <Trash />
                      </Icon>
                      {t('shared:form.delete')}
                    </Button>
                  )}
                </div>
              </div>
              <ErrorMessage error={errors.image} attribute={t('backend:image')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_default_individual_description">
                {t('backend:default_individual_description')}
              </InputLabel>
              <Textarea
                {...register('default_individual_description')}
                id="edit_project_default_individual_description"
                aria-invalid={!!errors.default_individual_description}
                placeholder={charity.default_individual_description}
                rows={3}
              />
              <ErrorMessage error={errors.default_individual_description} attribute={t('backend:default_individual_description')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_default_team_description">
                {t('backend:default_team_description')}
              </InputLabel>
              <Textarea
                {...register('default_team_description')}
                id="edit_project_default_team_description"
                aria-invalid={!!errors.default_team_description}
                placeholder={charity.default_team_description}
                rows={3}
              />
              <ErrorMessage error={errors.default_team_description} attribute={t('backend:default_team_description')} />
            </InputGroup>

            {projectTickets.length > 0 && (
              <InputGroup>
                <InputLabel valid={!errors.team_share_excluded_tickets}>
                  {t('backend:team_share_exclusions')}
                </InputLabel>
                <InputDescription>
                  {t('backend:team_share_exclusions_description')}
                </InputDescription>
                {projectTickets.map((ticket) => (
                  <CheckboxLabel htmlFor={`team_share_excluded_ticket_${ticket.id}`} key={ticket.id}>
                    <Checkbox
                      {...register('team_share_excluded_tickets')}
                      value={ticket.id}
                      id={`team_share_excluded_ticket_${ticket.id}`}
                      className="mt-1"
                    />
                    <span>
                      {ticket.title}
                    </span>
                  </CheckboxLabel>
                ))}
              </InputGroup>
            )}

            <InputGroup>
              <InputLabel htmlFor="edit_project_terms">
                {t('backend:terms_and_conditions')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_terms_and_conditions_description')}
              </InputDescription>
              <Input
                {...register('terms_url')}
                id="edit_project_terms"
                aria-invalid={!!errors.terms_url}
                placeholder="https://"
              />
              <ErrorMessage error={errors.terms_url} attribute={t('backend:terms_and_conditions')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:donations')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.fields} htmlFor="edit_project_donation_fields">
                {t('backend:project_donation_fields')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_donation_fields_description')}
              </InputDescription>
              {donationFields.map((donationField) => (
                <CheckboxLabel htmlFor={`edit_project_donation_fields_${donationField}`} key={donationField}>
                  <Checkbox
                    {...register('donation_fields')}
                    value={donationField}
                    id={`edit_project_donation_fields_${donationField}`}
                  />
                  <span>
                    {t(`backend:donation_fields.${donationField}`)}
                  </span>
                </CheckboxLabel>
              ))}
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_donation_message">
                {t('backend:donation_message')}
              </InputLabel>
              <InputDescription>
                {t('backend:donation_message_description')}
              </InputDescription>
              <Textarea
                {...register('donation_message')}
                id="edit_project_donation_message"
                aria-invalid={!!errors.donation_message}
                placeholder={charity.default_donation_message}
                rows={3}
              />
              <ErrorMessage error={errors.donation_message} attribute={t('backend:donation_message')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:mails')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel htmlFor="edit_project_email">
                {t('backend:email_address')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_email_description')}
              </InputDescription>
              <Input
                {...register('email')}
                id="edit_project_email"
                aria-invalid={!!errors.email}
                placeholder={charity.email || ''}
              />
              <ErrorMessage error={errors.email} attribute={t('backend:email_address')} />
            </InputGroup>
            <InputGroup>
              <InputLabel htmlFor="edit_project_mail_sender">
                {t('backend:mail_sender')}
              </InputLabel>
              <Input
                {...register('mail_sender')}
                id="edit_project_mail_sender"
                aria-invalid={!!errors.mail_sender}
                placeholder={charity?.mail_sender || charity.title}
              />
              <ErrorMessage error={errors.mail_sender} attribute={t('backend:mail_sender')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_header">
                {t('backend:project_mail_header')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_mail_header_description')}
              </InputDescription>
              <div className="max-w-[604px] p-4 border rounded-md border-slate-250">
                <div
                  className="flex items-center justify-center mb-4 transition-all rounded-sm bg-slate-50"
                >
                  {!mailHeader && !deleteMailHeader && currentMailHeaderUrl && (
                    <img
                      src={currentMailHeaderUrl}
                      alt={project.title}
                      className="w-full rounded-sm"
                    />
                  )}
                  {mailHeader && mailHeaderSrc && (
                    <img
                      src={mailHeaderSrc}
                      alt={project.title}
                      className="w-full rounded-sm"
                      onLoad={validateMailHeader}
                    />
                  )}
                  {((!mailHeader && !currentMailHeaderUrl) || deleteMailHeader) && (
                    <Icon className="!w-12 !h-12 text-slate-250 my-16">
                      <Image strokeWidth={1} />
                    </Icon>
                  )}
                </div>
                <div className="flex space-x-2">
                  <FileInput
                    {...register('mail_header')}
                    accept=".jpg,.jpeg,.png"
                    id="edit_project_mail_header"
                    aria-invalid={!!errors.mail_header}
                    onChange={handleMailHeaderChange}
                  >
                    <Icon className="mr-1">
                      <Image />
                    </Icon>
                    {t('shared:form.select')}
                  </FileInput>
                  {canDeleteMailHeader && (
                    <Button onClick={() => unsetMailHeader()} variant="tertiary" size="sm">
                      <Icon className="mr-1">
                        <Trash />
                      </Icon>
                      {t('shared:form.delete')}
                    </Button>
                  )}
                </div>
              </div>
              <ErrorMessage error={errors.mail_header} attribute={t('backend:project_mail_header')} />
            </InputGroup>
          </div>
        </div>

        {!project.default && (
          <div className="grid grid-cols-[1fr_3fr] gap-8">
            <div>
              <h4 className="font-medium text-slate-500 mb-1">
                {t('backend:restrictions')}
              </h4>
            </div>
            <div className="space-y-6">
              <InputGroup>
                <InputLabel valid={!errors.private} htmlFor="edit_project_private">
                  {t('backend:private_project')}
                </InputLabel>
                <InputDescription>
                  {t('backend:private_project_description')}
                </InputDescription>
                <CheckboxLabel htmlFor="edit_project_private">
                  <Checkbox
                    {...register('private')}
                    id="edit_project_private"
                    className="mt-1"
                  />
                  <span>
                    {t('backend:private_project_label')}
                  </span>
                </CheckboxLabel>
              </InputGroup>

              <InputGroup>
                <InputLabel valid={!errors.end_date} htmlFor="edit_project_end_date">
                  {t('backend:project_end_date')}
                </InputLabel>
                <InputDescription>
                  {t('backend:project_end_date_description')}
                </InputDescription>
                <div className="w-64">
                  <Controller
                    control={control}
                    name="end_date"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <DateInput
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        ariaInvalid={!!errors.end_date}
                        optional
                        id="edit_project_end_date"
                      />
                    )}
                  />
                </div>
                <ErrorMessage attribute={t('backend:project_end_date')} error={errors.end_date} />
              </InputGroup>

              <InputGroup>
                <InputLabel valid={!errors.open} htmlFor="edit_project_open">
                  {t('backend:open_project')}
                </InputLabel>
                <InputDescription>
                  {t('backend:open_project_description')}
                </InputDescription>
                <CheckboxLabel htmlFor="edit_project_open">
                  <Checkbox
                    checked={endDateIsPast ? false : open}
                    onChange={(event) => setValue('open', event.target.checked)}
                    disabled={endDateIsPast}
                    id="edit_project_open"
                    className="mt-1"
                  />
                  <span className={classNames(endDateIsPast && 'opacity-50')}>
                    {t('backend:open_project_label')}
                  </span>
                </CheckboxLabel>
              </InputGroup>
            </div>
          </div>
        )}

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:language')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel required valid={!errors.locale} htmlFor="edit_project_locale">
                {t('backend:language')}
              </InputLabel>
              <Select
                {...register('locale')}
                id="edit_project_locale"
                aria-invalid={!!errors.locale}
              >
                <option value={Locale.EN}>
                  {t(`shared:locales.${Locale.EN}`)}
                </option>
                <option value={Locale.NL}>
                  {t(`shared:locales.${Locale.NL}`)}
                </option>
              </Select>
              <ErrorMessage error={errors.locale} attribute={t('backend:language')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:payments')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.campaign_code} htmlFor="edit_project_campaign_code">
                {t('backend:campaign_code')}
              </InputLabel>
              <InputDescription>
                {t('backend:campaign_code_description')}
              </InputDescription>
              <Input
                {...register('campaign_code')}
                id="edit_project_campaign_code"
                aria-invalid={!!errors.campaign_code}
              />
              <ErrorMessage attribute={t('backend:campaign_code')} error={errors.campaign_code} />
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.payment_reference} htmlFor="edit_project_payment_reference">
                {t('backend:payment_reference')}
              </InputLabel>
              <InputDescription>
                {t('backend:payment_reference_description')}
                {' '}
                <span className={classNames('bg-blue-200 text-blue-700 px-1 inline-block text-sm rounded-sm', paymentReferenceLength === 0 && 'opacity-50')}>
                  <strong>{paymentReferenceLength}</strong> / {maxPaymentReferenceLength}
                </span>
              </InputDescription>
              <Input
                {...register('payment_reference')}
                id="edit_project_payment_reference"
                maxLength={maxPaymentReferenceLength}
                aria-invalid={!!errors.payment_reference}
                placeholder={charity.payment_reference || ''}
              />
              <div className="mt-2">
                <Trans i18nKey="backend:example_payment_reference" values={{ reference: `${paymentReference || charity.payment_reference || ''} 12345 ${project.title} ${charity.title}` }}>
                  <span className="font-medium text-slate-500"/>
                </Trans>
              </div>
              <ErrorMessage error={errors.payment_reference} attribute={t('backend:payment_reference')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:notifications')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.new_campaign_notification_users}>
                {t('backend:new_campaign_notification')}
              </InputLabel>
              <InputDescription>
                {t('backend:new_campaign_notification_description')}
              </InputDescription>
              {users.map((user) => (
                <CheckboxLabel htmlFor={`user_${user.id}`} key={user.id}>
                  <Checkbox
                    {...register('new_campaign_notification_users')}
                    value={user.id}
                    id={`user_${user.id}`}
                    className="mt-1"
                  />
                  <span>
                    {user.full_name}
                  </span>
                </CheckboxLabel>
              ))}
            </InputGroup>
          </div>
        </div>

        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}

interface CustomFieldFormProps {
  form: UseFormReturn<EditProjectFormData>;
  customFieldsArray: UseFieldArrayReturn<EditProjectFormData, 'custom_fields'>;
  index: number;
  project: EditProjectFormProps['project'];
}

const CustomFieldForm = ({
  form,
  customFieldsArray,
  index,
  project,
}: CustomFieldFormProps) => {
  const { t } = useTranslation();

  const { control, register, watch, getValues, setValue, trigger, formState: { errors } } = form;

  const path = `custom_fields.${index}` as const;

  const id = watch(`${path}.id`);
  const required = watch(`${path}.required`);
  const enabled = watch(`${path}.enabled`);
  const type = watch(`${path}.type`) as CustomFieldType;

  const [title, setTitle] = useState(customFieldsArray.fields[index].title);
  const [hovering, setHovering] = useState(false);
  const [editing, setEditing] = useState(!id && index === customFieldsArray.fields.length - 1);

  const customField = project.custom_fields.find((field) => field.id === id);
  const deletable = !customField || customField.deletable;
  const typeIsEditable = !id || deletable;
  const valid = !errors.custom_fields?.[index];
  const expanded = !valid || editing;

  const toggleForm = () => {
    if (expanded) {
      closeForm();
    } else {
      setEditing(true);
    }
  };

  const closeForm = async () => {
    const valid = await trigger(path);

    setEditing(!valid);
  };

  const updateTitle = useCallback(
    () => setTitle(getValues().custom_fields[index].title),
    [index]
  );

  useEffect(() => {
    updateTitle();
  }, [editing, updateTitle]);

  const customFieldTypes = [
    CustomFieldType.ShortText,
    CustomFieldType.Option,
    CustomFieldType.Selection,
  ];

  const moveUpCustomField = () => {
    customFieldsArray.swap(index, index - 1);
  };

  const moveDownCustomField = () => {
    customFieldsArray.swap(index, index + 1);
  };

  const deleteCustomField = () => {
    customFieldsArray.remove(index);
  };

  useEffect(() => {
    register(`${path}.required`);
    register(`${path}.enabled`);
  }, []);

  const disableCustomField = () => {
    setValue(`${path}.required`, false);
    setValue(`${path}.enabled`, false);
  };

  const enableCustomField = () => {
    setValue(`${path}.required`, false);
    setValue(`${path}.enabled`, true);
  };

  const requireCustomField = () => {
    setValue(`${path}.required`, true);
    setValue(`${path}.enabled`, true);
  };

  const choicesArray = useFieldArray({
    control,
    name: `${path}.choices`,
  });

  const addChoice = useCallback((count = 1) => {
    const focusIndex = choicesArray.fields.length;

    choicesArray.append(times(count, () => ({
      id: null,
      value: '',
    })), { focusIndex });
  }, [choicesArray.append, choicesArray.fields.length]);

  const hasChoices = [CustomFieldType.Option, CustomFieldType.Selection].includes(type);
  const minChoices = hasChoices ? (type === CustomFieldType.Option ? 2 : 1) : 0;

  const typeRef = useRef(type);

  // Ensure that the minimum number of choices is present.
  useEffect(() => {
    if (type !== typeRef.current) {
      if (hasChoices) {
        const choices = getValues().custom_fields[index].choices || [];
        const nonEmptyChoices = choices.filter((choice) => !!choice.value);

        const normalizedChoices = [
          ...nonEmptyChoices,
          ...times(Math.max(0, minChoices - nonEmptyChoices.length), () => ({
            id: null,
            value: '',
          })),
        ];

        if (
          normalizedChoices.length !== choices.length
          || normalizedChoices.some((choice, choiceIndex) => choice.value !== choices[choiceIndex]?.value)
        ) {
          choicesArray.replace(normalizedChoices);
        }
      } else {
        choicesArray.replace([]);
      }

      typeRef.current = type;
    }
    
  }, [hasChoices, type, minChoices, choicesArray, getValues, index]);

  const requiredLabel = !enabled
    ? t('backend:off')
    : required
      ? t('backend:required')
      : t('backend:optional');

  return (
    <div
      className={classNames(expanded && 'py-1')}
    >
      <div
        className={classNames(expanded && 'border border-slate-200 rounded-lg')}
      >
        <div
          className={classNames('grid grid-cols-[5fr_2fr_2fr_96px] gap-4 items-center', expanded && 'px-4 py-2 bg-slate-50 rounded-t-lg')}
        >
          <div>
            <a
              role="button"
              onClick={() => toggleForm()}
              className={classNames('font-medium cursor-pointer', enabled || expanded ? 'text-slate-800 hover:text-slate-800' : 'text-slate-400 hover:text-slate-400')}
              onMouseEnter={() => setHovering(true)}
              onMouseLeave={() => setHovering(false)}
            >
              {title || t('backend:untitled_field')}
              <Icon className={classNames('ml-2', hovering ? 'text-slate-800' : 'text-slate-400')}>
                {expanded ? <ChevronUp /> : <Edit />}
              </Icon>
            </a>
          </div>

          <div className="text-sm text-slate-500">
            {t(`backend:custom_field_types.${type}`)}
          </div>

          <div>
            <DropdownMenu
              trigger={({ onClick, isOpen }) => (
                <Button
                  onClick={onClick}
                  variant="tertiary"
                  className={classNames(isOpen && '!bg-slate-200 !border-slate-200')}
                  size="sm"
                  aria-label={`${requiredLabel} (${title || t('backend:untitled_field')})`}
                >
                  <Icon className="mr-1">
                    {enabled ? <Eye /> : <EyeOff/>}
                  </Icon>
                  {requiredLabel}
                  <Icon className="ml-1">
                    <ChevronDown />
                  </Icon>
                </Button>
              )}
              align="start"
            >
              <DropdownMenu.Item>
                <a
                  onClick={() => enableCustomField()}
                  role="button"
                >
                  {t('backend:optional')}
                </a>
              </DropdownMenu.Item>
              <DropdownMenu.Item>
                <a
                  onClick={() => requireCustomField()}
                  role="button"
                >
                  {t('backend:required')}
                </a>
              </DropdownMenu.Item>
              <DropdownMenu.Item>
                <a
                  onClick={() => disableCustomField()}
                  role="button"
                >
                  {t('backend:off')}
                </a>
              </DropdownMenu.Item>
            </DropdownMenu>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => moveUpCustomField()}
              variant="tertiary"
              size="sm"
              iconButton
              disabled={index === 0}
              aria-label={`${t('backend:move_up')} (${title || t('backend:untitled_field')})`}
            >
              <Icon>
                <ArrowUp />
              </Icon>
            </Button>
            <Button
              onClick={() => moveDownCustomField()}
              variant="tertiary"
              size="sm"
              iconButton
              disabled={index === customFieldsArray.fields.length - 1}
              aria-label={`${t('backend:move_down')} (${title || t('backend:untitled_field')})`}
            >
              <Icon>
                <ArrowDown />
              </Icon>
            </Button>
            <ConfirmButton
              onClick={() => deleteCustomField()}
              variant="tertiary"
              size="sm"
              iconButton
              disabled={!deletable}
              aria-label={`${t('backend:delete')} (${title || t('backend:untitled_field')})`}
            >
              <Icon>
                <Trash />
              </Icon>
            </ConfirmButton>
          </div>
        </div>

        <FadeIn
          when={expanded}
          render={() => (
            <div className="border-t border-slate-200 p-6 space-y-6">
              <InputGroup>
                <InputLabel required valid={!errors.custom_fields?.[index]?.title} htmlFor={`edit_custom_field_title_${index}`}>
                  {t('backend:custom_field_title')}
                </InputLabel>
                <Input
                  {...register(`${path}.title`, { onBlur: () => updateTitle() })}
                  id={`edit_custom_field_title_${index}`}
                  aria-invalid={!!errors.custom_fields?.[index]?.title}
                />
                <ErrorMessage error={errors.custom_fields?.[index]?.title} attribute={t('backend:custom_field_title')} />
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor={`edit_custom_field_description_${index}`}>
                  {t('backend:custom_field_description')}
                </InputLabel>
                <Textarea
                  {...register(`${path}.description`)}
                  id={`edit_custom_field_description_${index}`}
                  aria-invalid={!!errors.custom_fields?.[index]?.description}
                  minRows={2}
                />
                <ErrorMessage error={errors.custom_fields?.[index]?.description} attribute={t('backend:custom_field_description')} />
              </InputGroup>

              {typeIsEditable && (
                <InputGroup>
                  <InputLabel htmlFor="edit_custom_field_type">
                    {t('backend:custom_field_type')}
                  </InputLabel>
                  <div className="flex space-x-3">
                    {customFieldTypes.map((fieldType) => (
                      <RadioLabel key={fieldType}>
                        <Radio
                          {...register(`${path}.type`)}
                          value={fieldType}
                        />
                        <span>
                          {t(`backend:custom_field_types.${fieldType}`)}
                        </span>
                      </RadioLabel>
                    ))}
                  </div>
                </InputGroup>
              )}

              {hasChoices && (
                <InputGroup>
                  <InputLabel htmlFor="edit_custom_field_choices">
                    {t('backend:choices')}
                  </InputLabel>
                  {type === CustomFieldType.Option && (
                    <InputDescription>
                      {t('backend:custom_field_type_descriptions.choice')}
                    </InputDescription>
                  )}
                  {type === CustomFieldType.Selection && (
                    <InputDescription>
                      {t('backend:custom_field_type_descriptions.selection')}
                    </InputDescription>
                  )}
                  <div className="space-y-1">
                    {choicesArray.fields.map((field, choiceIndex) => (
                      <ChoiceForm
                        form={form}
                        choicesArray={choicesArray}
                        fieldIndex={index}
                        choiceIndex={choiceIndex}
                        customField={customField}
                        minChoices={minChoices}
                        key={field.id}
                      />
                    ))}
                  </div>
                  <Button onClick={() => addChoice()} variant="secondary" className="mt-2">
                    <Icon className="mr-1">
                      <Plus />
                    </Icon>
                    {t('backend:add_choice')}
                  </Button>
                </InputGroup>
              )}

              <hr />

              <div>
                <Button onClick={() => closeForm()} variant="secondary">
                  <Icon className="mr-1">
                    <Check />
                  </Icon>
                  {t('backend:confirm')}
                </Button>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
};

type ChoicesPath = `custom_fields.${number}.choices`;

interface ChoiceFormProps {
  form: UseFormReturn<EditProjectFormData>;
  choicesArray: UseFieldArrayReturn<EditProjectFormData, ChoicesPath>;
  fieldIndex: number;
  choiceIndex: number;
  minChoices: number;
  customField?: EditProjectFormProps['project']['custom_fields'][0] | null;
}

const ChoiceForm = ({
  form,
  choicesArray,
  fieldIndex,
  choiceIndex,
  customField,
  minChoices,
}: ChoiceFormProps) => {
  const { t } = useTranslation();

  const { register, watch, formState: { errors } } = form;

  const path = `custom_fields.${fieldIndex}.choices.${choiceIndex}` as const;

  const id = watch(`${path}.id`);

  const choice = customField?.choices.find((choice) => choice.id === id);
  const deletable = !choice || choice.deletable;

  const moveUpChoice = () => {
    choicesArray.swap(choiceIndex, choiceIndex - 1);
  };

  const moveDownChoice = () => {
    choicesArray.swap(choiceIndex, choiceIndex + 1);
  };

  const deleteChoice = () => {
    choicesArray.remove(choiceIndex);
  };

  return (
    <div className="grid grid-cols-[1fr_1fr] gap-4 items-center">
      <div>
        <Input
          {...register(`${path}.value`)}
          id={`edit_custom_fields_${fieldIndex}_choices_${fieldIndex}_value`}
          aria-invalid={!!errors.custom_fields?.[fieldIndex]?.choices?.[choiceIndex]?.value}
          aria-label={t('backend:choice_n', { n: choiceIndex + 1 })}
        />
        <ErrorMessage error={errors.custom_fields?.[fieldIndex]?.choices?.[choiceIndex]?.value} attribute={t('backend:choice')} />
      </div>
      <div>
        <Button
          onClick={() => moveUpChoice()}
          variant="tertiary"
          size="sm"
          iconButton
          disabled={choiceIndex === 0}
          aria-label={`${t('backend:move_up')} (${t('backend:choice_n', { n: choiceIndex + 1 })})`}
        >
          <Icon>
            <ArrowUp />
          </Icon>
        </Button>
        <Button
          onClick={() => moveDownChoice()}
          variant="tertiary"
          size="sm"
          iconButton
          disabled={choiceIndex === choicesArray.fields.length - 1}
          aria-label={`${t('backend:move_down')} (${t('backend:choice_n', { n: choiceIndex + 1 })})`}
        >
          <Icon>
            <ArrowDown />
          </Icon>
        </Button>
        <ConfirmButton
          onClick={() => deleteChoice()}
          variant="tertiary"
          size="sm"
          iconButton
          disabled={choicesArray.fields.length <= minChoices || !deletable}
          aria-label={`${t('backend:delete')} (${t('backend:choice_n', { n: choiceIndex + 1 })})`}
        >
          <Icon>
            <Trash />
          </Icon>
        </ConfirmButton>
      </div>
    </div>
  );
};
